import { Injectable, effect, inject, signal } from '@angular/core';
import { AuthService } from './auth.service';

const DEFAULT_PREFERRED_URL = '/landing';

@Injectable({ providedIn: 'root' })
export class SessionService {
  private readonly auth = inject(AuthService);

  private readonly _userName = signal('Guest');
  private readonly _userSurname = signal('');
  private readonly _userOrg = signal('');
  private readonly _avatarUrl = signal<string | null>('');
  private readonly _isPilot = signal(false);
  private readonly _isManager = signal(false);
  private readonly _isAdmin = signal(false);
  private readonly _isSuperAdmin = signal(false);
  private readonly _userId = signal('');
  private readonly _orgId = signal(0);
  private readonly _preferredUrl = signal(DEFAULT_PREFERRED_URL);

  readonly userName = this._userName.asReadonly();
  readonly userSurname = this._userSurname.asReadonly();
  readonly userOrg = this._userOrg.asReadonly();
  readonly avatarUrl = this._avatarUrl.asReadonly();
  readonly isPilot = this._isPilot.asReadonly();
  readonly isManager = this._isManager.asReadonly();
  readonly isAdmin = this._isAdmin.asReadonly();
  readonly isSuperAdmin = this._isSuperAdmin.asReadonly();
  readonly userId = this._userId.asReadonly();
  readonly orgId = this._orgId.asReadonly();
  readonly preferredUrl = this._preferredUrl.asReadonly();

  constructor() {
    // Re-derive whenever the effective auth user changes (login/logout), instead of
    // snapshotting once — the header renders before the login callback populates
    // the user, so a one-shot read here would permanently latch onto 'Guest'.
    effect(() => this.refresh());
  }

  refresh(): void {
    const user = this.auth.getUser();
    this._userName.set(user?.per_name || (user ? 'User' : 'Guest'));
    this._userSurname.set(user?.per_surname || '');
    this._userOrg.set(user?.org_name || (user ? 'Organization' : ''));
    this._isPilot.set(this.auth.isPilot());
    this._isManager.set(this.auth.isManager());
    this._isAdmin.set(this.auth.isAdmin());
    this._isSuperAdmin.set(this.auth.isSuperAdmin());
    this._userId.set(this.auth.getUserId());
    this._orgId.set(user?.org_id || 0);
    this._preferredUrl.set(user?.preferred_url || DEFAULT_PREFERRED_URL);
  }

  async loadAvatar(): Promise<void> {
    try {
      this._avatarUrl.set(await this.auth.getUserProfilePictureUrl());
    } catch {
      this._avatarUrl.set('');
    }
  }
}
